/**
 * CommonControls.tsx — Workspace-level common control policies.
 *
 * Policy Leads manage policies here. Each policy covers one or more
 * controls across systems. System Leads accept these from their system
 * worklist.
 *
 * Route: /w/:slug/app/common-controls
 */

import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  ControlPolicyStatusToken,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  listCommonControlPolicies,
  createCommonControlPolicy,
  listAllControls,
  req,
  type CommonControlPolicyRow,
} from '../lib/api';
import { fmtDate } from '../lib/format';

const SCOPE_LABELS: Record<string, string> = {
  all: 'All systems',
  classification: 'By classification',
  manual: 'Manual assignment',
};

export function CommonControls() {
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  // V96-4-9: SOC 2 "Map a control" deep-links with ?add=1.
  const [searchParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(searchParams.get('add') === '1');

  const q = useWorkspaceQuery(
    (s) => queryKeys.commonControls(s),
    (s) => listCommonControlPolicies(s),
  );
  const policies = q.data?.policies ?? [];

  if (q.loading) return <Spinner label="Loading policies" />;
  if (q.error) return <Alert variant="error">{String(q.error)}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <PageHeader
          title="Common Controls"
          subtitle="Workspace-wide policies that cover controls across AI systems. System leads accept these from their system worklist."
        />
        {!addOpen ? (
          <Button variant="primary" onClick={() => setAddOpen(true)}>Add policy</Button>
        ) : null}
      </div>

      {addOpen ? (
        <AddPolicyForm
          slug={slug}
          onCreated={() => { setAddOpen(false); q.refetch(); }}
          onCancel={() => setAddOpen(false)}
        />
      ) : null}

      {policies.length === 0 && !addOpen ? (
        <EmptyState
          title="No common policies"
          description="Create a workspace-wide policy to cover controls across your AI systems. System leads will see these as available when managing their system controls."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {policies.map((p) => (
            <PolicyCard key={p.policy_id} policy={p} />
          ))}
        </div>
      )}
    </div>
  );
}

const FAMILY_LABELS: Record<string, string> = {
  ogcf: 'Cross-cutting (OGCF)',
  ml_models: 'ML Models',
  llm_services: 'LLM Services',
  ai_agents: 'AI Agents',
  agentic_systems: 'Agentic Systems',
  data_pipelines: 'Data Pipelines',
};

function AddPolicyForm({ slug, onCreated, onCancel }: {
  slug: string; onCreated: () => void; onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [scopeType, setScopeType] = useState<'all' | 'classification' | 'manual'>('all');
  const [docMode, setDocMode] = useState<'none' | 'url' | 'upload'>('none');
  const [docUrl, setDocUrl] = useState('');
  const [docFile, setDocFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [familyFilter, setFamilyFilter] = useState('all');

  const controlsQ = useWorkspaceQuery(
    (s) => queryKeys.allControls(s),
    (s) => listAllControls(s),
  );
  const allControls = controlsQ.data?.controls ?? [];

  const filtered = useMemo(() => {
    let items = allControls;
    if (familyFilter !== 'all') items = items.filter((c) => c.family === familyFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((c) =>
        c.control_id.toLowerCase().includes(q) ||
        c.title.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [allControls, familyFilter, search]);

  const families = useMemo(() => {
    const set = new Set(allControls.map((c) => c.family));
    return Array.from(set).sort();
  }, [allControls]);

  const toggleControl = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleCreate = useCallback(async () => {
    if (!title.trim() || selectedIds.size === 0) return;
    setBusy(true); setError(null);
    try {
      let artifactId: string | undefined;

      if (docMode === 'url' && docUrl.trim()) {
        const data = await req<{ artifact: { artifact_id: string } }>(`/api/w/${encodeURIComponent(slug)}/evidence`, {
          method: 'POST',
          body: JSON.stringify({
            type: 'policy',
            title: title.trim(),
            artifact_ref: docUrl.trim(),
            artifact_ref_kind: 'url',
          }),
        });
        artifactId = data.artifact.artifact_id;
      } else if (docMode === 'upload' && docFile) {
        const form = new FormData();
        form.append('title', title.trim());
        form.append('type', 'policy');
        form.append('file', docFile);
        const resp = await fetch(`/api/w/${encodeURIComponent(slug)}/evidence/upload`, { // raw-fetch-ok: FormData multipart upload
          method: 'POST',
          body: form,
        });
        if (!resp.ok) throw new Error('Failed to upload policy document');
        const data = await resp.json() as { artifact: { artifact_id: string } };
        artifactId = data.artifact.artifact_id;
      }

      await createCommonControlPolicy(slug, {
        title: title.trim(),
        description: description.trim() || undefined,
        control_ids: Array.from(selectedIds),
        scope_type: scopeType,
        artifact_id: artifactId,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  }, [slug, title, description, selectedIds, scopeType, docMode, docUrl, docFile, onCreated]);

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
      background: 'var(--surface)', padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)',
    }}>
      <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
        Add common control policy
      </div>
      <Field id="pol-title" label="Policy title">
        <input id="pol-title" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Identity & Access Management Policy"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)',
            color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
      </Field>
      <Field id="pol-desc" label="Description (optional)">
        <textarea id="pol-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="What this policy covers and when it applies"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)',
            color: 'var(--text)', fontSize: 'var(--font-size-2xs)', resize: 'vertical' }} />
      </Field>

      {/* Control picker */}
      <div>
        <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
          Controls this policy covers ({selectedIds.size} selected)
        </div>

        {/* Selected chips */}
        {selectedIds.size > 0 ? (
          <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
            {Array.from(selectedIds).sort().map((id) => {
              const ctrl = allControls.find((c) => c.control_id === id);
              return (
                <button
                  key={id}
                  onClick={() => toggleControl(id)}
                  title={`Remove ${id}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '2px 8px', borderRadius: 'var(--radius-chip)',
                    border: '1px solid var(--accent)', background: 'var(--accent-soft, var(--surface-elevated))',
                    color: 'var(--accent)', fontSize: 'var(--font-size-3xs)',
                    fontFamily: 'var(--font-mono)', cursor: 'pointer',
                  }}
                >
                  {id} {ctrl ? `· ${ctrl.title.slice(0, 30)}` : ''} ×
                </button>
              );
            })}
          </div>
        ) : null}

        {/* Search + family filter */}
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search controls..."
            style={{
              flex: 1, padding: 'var(--space-1) var(--space-2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-input, var(--radius-card))',
              background: 'var(--surface)', color: 'var(--text)',
              fontSize: 'var(--font-size-2xs)',
            }}
          />
          <Select value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}
            style={{ fontSize: 'var(--font-size-2xs)' }}>
            <option value="all">All families</option>
            {families.map((f) => (
              <option key={f} value={f}>{FAMILY_LABELS[f] ?? f}</option>
            ))}
          </Select>
        </div>

        {/* Control list */}
        <div style={{
          maxHeight: 280, overflowY: 'auto',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
          background: 'var(--surface-elevated, var(--surface))',
        }}>
          {controlsQ.loading ? <Spinner /> : filtered.length === 0 ? (
            <div style={{ padding: 'var(--space-3)', color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)', textAlign: 'center' }}>
              No controls match your search.
            </div>
          ) : (
            filtered.map((ctrl) => {
              const checked = selectedIds.has(ctrl.control_id);
              const frameworks = (() => { try { return JSON.parse(ctrl.framework_refs ?? '[]') as string[]; } catch { return []; } })();
              return (
                <label
                  key={ctrl.control_id}
                  style={{
                    display: 'grid', gridTemplateColumns: '20px 1fr',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-2)',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: checked ? 'var(--accent-soft, transparent)' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleControl(ctrl.control_id)}
                    style={{ marginTop: 2 }}
                  />
                  <div style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontWeight: 600,
                        color: 'var(--text)', fontSize: 'var(--font-size-3xs)',
                        flexShrink: 0,
                      }}>
                        {ctrl.control_id}
                      </span>
                      <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text)', fontWeight: 500 }}>
                        {ctrl.title}
                      </span>
                    </div>
                    {ctrl.description ? (
                      <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', lineHeight: 1.3 }}>
                        {ctrl.description}
                      </span>
                    ) : null}
                    {frameworks.length > 0 ? (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
                        {frameworks.map((fw) => (
                          <span key={fw} style={{
                            padding: '0 4px', borderRadius: 'var(--radius-chip)',
                            border: '1px solid var(--border)', fontSize: '10px',
                            color: 'var(--text-dim)', lineHeight: '16px',
                          }}>
                            {fw}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>

      {/* Policy document */}
      <div>
        <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
          Policy document (optional)
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
          {(['none', 'url', 'upload'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setDocMode(mode)}
              style={{
                padding: '4px 12px', borderRadius: 'var(--radius-chip)',
                border: docMode === mode ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: docMode === mode ? 'var(--accent-soft, var(--surface-elevated))' : 'var(--surface)',
                color: docMode === mode ? 'var(--accent)' : 'var(--text-muted)',
                fontSize: 'var(--font-size-3xs)', cursor: 'pointer', fontFamily: 'var(--font-ui)',
              }}
            >
              {mode === 'none' ? 'No document' : mode === 'url' ? 'Link (URL)' : 'Upload file'}
            </button>
          ))}
        </div>
        {docMode === 'url' ? (
          <input
            value={docUrl}
            onChange={(e) => setDocUrl(e.target.value)}
            placeholder="https://docs.example.com/iam-policy"
            style={{
              width: '100%', padding: 'var(--space-2) var(--space-3)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-input, var(--radius-card))',
              background: 'var(--surface)', color: 'var(--text)',
              fontSize: 'var(--font-size-2xs)',
            }}
          />
        ) : null}
        {docMode === 'upload' ? (
          <div>
            <input
              type="file"
              accept=".pdf,.docx,.md,.txt"
              onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
              style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}
            />
            {docFile ? (
              <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', marginLeft: 'var(--space-2)' }}>
                {docFile.name} ({(docFile.size / 1024).toFixed(0)} KB)
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <Field id="pol-scope" label="Scope">
        <Select id="pol-scope" value={scopeType} onChange={(e) => setScopeType(e.target.value as any)}>
          <option value="all">All systems</option>
          <option value="classification">By classification (filtered)</option>
          <option value="manual">Manual — system leads opt in</option>
        </Select>
      </Field>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button variant="primary" compact disabled={busy || !title.trim() || selectedIds.size === 0} onClick={handleCreate}>
          {busy ? 'Creating...' : `Create policy (${selectedIds.size} controls)`}
        </Button>
        <Button variant="ghost" compact disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function PolicyCard({ policy }: { policy: CommonControlPolicyRow }) {
  const [expanded, setExpanded] = useState(false);
  const controlIds = useMemo(() => {
    try { return JSON.parse(policy.control_ids_json) as string[]; } catch { return []; }
  }, [policy.control_ids_json]);

  return (
    <Card>
      <div
        style={{ padding: 'var(--space-3) var(--space-4)', cursor: 'pointer' }}
        onClick={() => setExpanded((p) => !p)}
        role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((p) => !p); } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>{policy.title}</span>
            <Badge>{SCOPE_LABELS[policy.scope_type] ?? policy.scope_type}</Badge>
            <Badge>{controlIds.length} control{controlIds.length !== 1 ? 's' : ''}</Badge>
          </div>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            {policy.last_attested_at ? `Attested ${fmtDate(policy.last_attested_at)}` : 'Not attested'}
            <span aria-hidden="true"> {expanded ? '▾' : '▸'}</span>
          </span>
        </div>

        {expanded ? (
          <div style={{ marginTop: 'var(--space-2)', display: 'grid', gap: 'var(--space-1)', fontSize: 'var(--font-size-2xs)' }}>
            {policy.description ? (
              <p style={{ margin: 0, color: 'var(--text-muted)' }}>{policy.description}</p>
            ) : null}
            <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
              {controlIds.map((id) => (
                <span key={id} style={{
                  fontFamily: 'var(--font-mono)', padding: '1px 6px',
                  borderRadius: 'var(--radius-chip)', border: '1px solid var(--border)',
                  fontSize: 'var(--font-size-3xs)', color: 'var(--text)',
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                }}>
                  {id}
                  {/* V91-4-9: spec availability indicator — baseline_available is a safe
                     default; once the spec-status API surfaces per-control state this
                     should read from it. */}
                  <ControlPolicyStatusToken status="baseline_available" />
                </span>
              ))}
            </div>
            {policy.artifact_id ? (
              <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--accent)' }}>
                Policy document linked
              </div>
            ) : (
              <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                No document attached
              </div>
            )}
            <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
              Cadence: {policy.attestation_cadence_months} months · Created {fmtDate(policy.created_at)}
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
